import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { ReceivingTrade } from '../ActiveReceivingPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { useCompleteTradeMutation, useGenerateShareImageMutation } from 'store/services/trade/tradeApi';
import { useSubmitTradeRatingMutation } from 'store/services/trade/tradeRatingApi';
import { useGetUiPreferencesQuery, useUpdateUiPreferencesMutation } from 'store/services/settings/uiPreferencesApi';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { toast } from 'react-toastify';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import SendPointsModal from 'components/SendPointsModal/SendPointsModal';
import { ReceivingFundingNotice } from 'components/AwaitingFundingNotice/AwaitingFundingNotice';
import FirstTradeConfirmModal from './FirstTradeConfirmModal';
import ShareIcon from 'components/Icons/ShareIcon';
import TradeShareModal from './TradeShareModal';
import { ReceivingTradeItem } from '../ActiveReceivingPage';
import { AssetUrls } from 'config/assets';
import arevakhachIcon from 'assets/icons/arevakhach-icon.svg';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { SetIcon } from 'components/SetIcon';
import { FoilCardImage } from 'components/FoilCardImage';
import CardImageTooltip from 'components/CardLink/CardImageTooltip';
import { getCardImageUrl, makeCardImageErrorHandler } from 'utils/cardImageFallback';
import { formatLanguage } from 'constants/languages';
import ViewModeDropdown from 'components/ViewModeDropdown/ViewModeDropdown';
import {
  useTradeDetailViewMode,
  TradeDetailViewMode,
  TRADE_DETAIL_VIEW_OPTIONS,
} from 'hooks/useTradeDetailViewMode';
import './ReceivingTradeCard.css';
import { finishFromRow } from 'utils/finishes';
import { formatTimeToShip, getReceivingShipWindow } from '../utils/receivingShipWindow';
import { FinishTag } from 'components/FinishTag';

type SortKey = 'dateAdded' | 'name' | 'set' | 'points';
type SortDir = 'asc' | 'desc';

function SortArrow({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) {
    return (
      <svg className="col-sort-arrow" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
        <path d="M2 4l3-3 3 3M2 6l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" opacity="0.35" />
      </svg>
    );
  }
  return (
    <svg className="col-sort-arrow col-sort-arrow--active" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
      {dir === 'asc' ? (
        <path d="M2 6l3-3 3 3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M2 4l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

interface Props {
  trade: ReceivingTrade;
  onFirstTradeCompleted?: (shareImageUrl: string | null, cards: ReceivingTradeItem[]) => void;
}

interface Ratings {
  speed: number;
  accuracy: number;
  communication: number;
}

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM',
    'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'LIGHTLY_PLAYED': 'LP',
    'LP': 'LP',
    'SP': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'MP': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'HP': 'HP',
    'DAMAGED': 'DMG',
    'DMG': 'DMG'
  };
  return conditionMap[condition] || condition;
};

export default function ReceivingTradeCard({ trade, onFirstTradeCompleted }: Props) {
  const [ratings, setRatings] = useState<Ratings>({
    speed: trade.speed,
    accuracy: trade.accuracy,
    communication: trade.communication
  });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSendPointsOpen, setIsSendPointsOpen] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<{
    scryfallId?: string;
    imageUrl?: string;
    cardName: string;
    position: { x: number; y: number };
  } | null>(null);
  const handleCardHover = (e: React.MouseEvent, item: ReceivingTradeItem) => {
    setHoveredCard({
      scryfallId: item.scryfallId,
      imageUrl: getCardImageUrl({ image: item.image, scryfallId: item.scryfallId }, 'normal'),
      cardName: item.name,
      position: { x: e.clientX, y: e.clientY },
    });
  };
  const handleCardLeave = () => setHoveredCard(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState<string | null>(null);
  const [completeTrade, { isLoading: isCompletingTrade }] = useCompleteTradeMutation();
  const [generateShareImage, { isLoading: isGeneratingShareImage }] = useGenerateShareImageMutation();
  const cardImageFallbackRef = useRef<Map<string | number, number>>(new Map());
  const [viewMode, setViewMode] = useTradeDetailViewMode();
  const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'dateAdded', dir: 'asc' });

  const handleHeaderSort = (key: Exclude<SortKey, 'dateAdded'>) => {
    setSort(prev => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key, dir: 'asc' };
    });
  };

  const orderedItems = useMemo(() => {
    const indexed = trade.items.map((item, index) => ({ item, index }));
    if (sort.key === 'dateAdded') return indexed;
    const dirMultiplier = sort.dir === 'asc' ? 1 : -1;
    const compare = (a: { item: ReceivingTradeItem }, b: { item: ReceivingTradeItem }) => {
      switch (sort.key) {
        case 'name':
          return a.item.name.localeCompare(b.item.name) * dirMultiplier;
        case 'set': {
          const aSet = (a.item.setCode || a.item.set || '').toLowerCase();
          const bSet = (b.item.setCode || b.item.set || '').toLowerCase();
          return aSet.localeCompare(bSet) * dirMultiplier;
        }
        case 'points': {
          const aPts = (a.item.pointValue || 0) * a.item.qty;
          const bPts = (b.item.pointValue || 0) * b.item.qty;
          return (aPts - bPts) * dirMultiplier;
        }
        default:
          return 0;
      }
    };
    return [...indexed].sort(compare);
  }, [trade.items, sort]);

  const [submitRating] = useSubmitTradeRatingMutation();
  const { data: uiPrefs } = useGetUiPreferencesQuery();
  const [updateUiPreferences] = useUpdateUiPreferencesMutation();
  const navigate = useNavigate();

  // Debounce rating changes (1 second delay)
  const debouncedRatings = useDebouncedValue(ratings, 1000);

  // Tick every 60s so the shipping countdown re-renders while card is mounted
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (trade.status !== 'preparing') return;
    const interval = setInterval(() => setNowTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(interval);
  }, [trade.status]);

  // Anchored on shipWindowStart (openedAt ?? initiatedAt), the same anchor as
  // the Sending card's countdown and the panels, never on initiated.
  const shippingInfo = getReceivingShipWindow(trade.shipWindowStart, Date.now());

  // Track if this is the initial mount (skip auto-submit on load)
  const isInitialMount = useRef(true);
  const hasUserChangedRating = useRef(false);
  const celebrationFiredRef = useRef(false);

  // Submit rating when debounced value changes (user clicked stars)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (trade.status === 'completed' && hasUserChangedRating.current) {
      submitRating({
        tradeId: trade.id,
        speed: debouncedRatings.speed,
        accuracy: debouncedRatings.accuracy,
        communication: debouncedRatings.communication,
      }).catch((error) => {
        console.error('Failed to submit rating:', error);
        toast.error('Failed to save rating');
      });
    }
  }, [debouncedRatings, trade.id, trade.status, submitRating]);

  const executeTradeCompletion = async () => {
    try {
      const tradeId = String(trade.id);
      const result = await completeTrade({ tradeId }).unwrap();

      if (result.success) {
        // Show fee information if available
        if (result.tradeFee && result.tradeFee > 0) {
          toast.success(`Trade completed! ${result.pointsCredited || 0} points credited to sender (${result.tradeFee} point fee deducted)`);
        } else {
          toast.success(`Trade completed! ${result.pointsCredited || 0} points credited to sender.`);
        }

        // Auto-submit 5-star rating on trade completion
        submitRating({
          tradeId: String(trade.id),
          speed: 5,
          accuracy: 5,
          communication: 5,
        }).catch((error) => {
          console.error('Failed to auto-submit rating:', error);
        });

        // Notify parent for first trade celebration (lifted to page level to survive unmount)
        if (result.isFirstTrade && !uiPrefs?.hasSeenFirstTradeCelebration && !celebrationFiredRef.current) {
          celebrationFiredRef.current = true;
          try {
            await updateUiPreferences({ hasSeenFirstTradeCelebration: true }).unwrap();
          } catch {
            // Continue even if preferences update fails - local ref prevents re-trigger
          }
          onFirstTradeCompleted?.(result.shareImageUrl || null, trade.items);
        }
      } else {
        toast.error(result.message || 'Failed to complete trade');
      }
    } catch (error: any) {
      console.error('Error completing trade:', error);
      if (error?.status === 500) {
        toast.error('Server error occurred. The backend may need to be checked.');
      } else {
        toast.error(error?.data?.message || 'Failed to complete trade');
      }
    }
  };

  const handleCompleteTrade = () => {
    // Check if trade is in a valid status to complete
    if (trade.status !== 'transit' && trade.status !== 'preparing') {
      toast.warning(`Cannot complete trade in ${trade.status} status.`);
      return;
    }

    // Show first-time confirmation modal if user hasn't seen it
    // If preferences are still loading (undefined), skip the modal to avoid blocking
    if (uiPrefs && !uiPrefs.hasSeenTradeConfirmModal) {
      updateUiPreferences({ hasSeenTradeConfirmModal: true });
      setShowConfirmModal(true);
      return;
    }

    executeTradeCompletion();
  };

  const handleConfirmArrived = () => {
    setShowConfirmModal(false);
    executeTradeCompletion();
  };

  const handleContact = () => {
    setIsContactModalOpen(true);
  };

  const handleSayThanks = () => {
    setIsContactModalOpen(true);
  };

  const handleTradeAgain = () => {
    const params = new URLSearchParams({ searchType: 'user', search: trade.traderName });
    navigate(`/sendcards?${params.toString()}`);
  };

  const handleShareTrade = async () => {
    setIsShareModalOpen(true);
    if (!shareImageUrl) {
      try {
        const result = await generateShareImage({ tradeId: trade.id }).unwrap();
        setShareImageUrl(result.shareImageUrl);
      } catch {
        toast.error('Failed to generate share image');
      }
    }
  };
  const getStatusTitle = () => {
    switch (trade.status) {
      case 'preparing':
        return trade.awaitingFunding ? 'Waiting to open' : 'Confirmed';
      case 'transit':
        return 'Shipped';
      case 'completed':
        return 'Arrived';
      case 'cancelled':
        return trade.isRemovedItem ? 'Removed' : 'Cancelled';
      default:
        return '';
    }
  };

  const getStatusLabel = () => {
    switch (trade.status) {
      case 'preparing':
        return 'You will receive:';
      case 'transit':
        return 'You will receive:';
      case 'completed':
        return 'You received:';
      case 'cancelled':
        return trade.isRemovedItem ? 'You were receiving:' : 'You were receiving:';
      default:
        return '';
    }
  };

  const getTimeDisplay = () => {
    switch (trade.status) {
      case 'preparing': {
        // A leg awaiting funding has no ship clock — nothing is overdue while
        // it waits for the funding trade to complete.
        if (trade.awaitingFunding) {
          return trade.fundedByTradeNumber
            ? `Opens after Trade #${trade.fundedByTradeNumber} completes`
            : 'Opens after its funding trade completes';
        }
        return formatTimeToShip(shippingInfo);
      }
      case 'transit':
        return `Estimated time to arrive: ${trade.estimatedDelivery}`;
      case 'completed':
        return `Arrived: ${trade.completed}`;
      default:
        return '';
    }
  };

  const handleStarClick = (category: keyof Ratings, starIndex: number) => {
    hasUserChangedRating.current = true;
    setRatings(prev => ({
      ...prev,
      [category]: starIndex
    }));
  };

  const renderStarRating = (category: keyof Ratings, rating: number) => {
    // Ensure rating is between 0 and 5
    const safeRating = Math.max(0, Math.min(5, rating || 0));
    const emptyStars = Math.max(0, 5 - safeRating);
    
    if (trade.status !== 'completed') {
      return '★'.repeat(safeRating) + '☆'.repeat(emptyStars);
    }

    return (
      <div className="interactive-stars">
        {[1, 2, 3, 4, 5].map(starIndex => (
          <span
            key={starIndex}
            className={`star ${starIndex <= ratings[category] ? 'filled' : 'empty'}`}
            onClick={() => handleStarClick(category, starIndex)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="receiving-trade-card">
      <div className="receiving-trade-card-left">
        <div className="package-title-row">
          <h3 className="trade-status-title">{getStatusTitle()}</h3>
          <Points points={trade.pendingValue ?? trade.totalValue} type="big" />
        </div>
        
        <div className="trade-info">
          <div className="trade-detail">
            <span className="trade-label">Trade#</span>
            <span className="trade-value">{trade.tradeNumber}</span>
          </div>
          <div className="trade-detail">
            <span className="trade-label">Initiated:</span>
            <span className="trade-value">{trade.initiated}</span>
          </div>
          {trade.shipped && (
            <div className="trade-detail">
              <span className="trade-label">Shipped:</span>
              <span className="trade-value">{trade.shipped}</span>
            </div>
          )}
          {trade.completed && (
            <div className="trade-detail">
              <span className="trade-label">Completed:</span>
              <span className="trade-value">{trade.completed}</span>
            </div>
          )}
          {trade.cancelled && (
            <div className="trade-detail">
              <span className="trade-label">{trade.isRemovedItem ? 'Removed:' : 'Cancelled:'}</span>
              <span className="trade-value">{trade.cancelled}</span>
            </div>
          )}
          {trade.isRemovedItem && trade.removedFromTradeNumber && (
            <div className="trade-detail">
              <span className="trade-label">Removed from:</span>
              <span className="trade-value">Trade #{trade.removedFromTradeNumber}</span>
            </div>
          )}
        </div>

        <div className="time-display">
          <div className="time-label">{getTimeDisplay()}</div>
          {trade.status === 'preparing' && !trade.awaitingFunding && (
            <div className="progress-bar-container">
              <div
                className={`progress-fill${shippingInfo?.isExpired ? ' overdue' : ''}`}
                style={{ width: `${shippingInfo?.isExpired ? 100 : shippingInfo?.percentage ?? 0}%` }}
              ></div>
            </div>
          )}
          {trade.status === 'preparing' && trade.awaitingFunding && (
            <ReceivingFundingNotice
              fundedByTradeNumber={trade.fundedByTradeNumber}
              reservedPoints={trade.reservedPoints}
              totalValue={trade.totalValue}
            />
          )}
          {trade.status === 'transit' && (
            <div className="progress-bar-container">
              <div className="progress-fill" style={{ width: '66%' }}></div>
            </div>
          )}
        </div>

        {trade.status === 'completed' && (
          <div className="trader-rating">
            <div className="rating-row">
              <span className="rating-label">Speed:</span>
              <span className="rating-stars">{renderStarRating('speed', ratings.speed)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Accuracy:</span>
              <span className="rating-stars">{renderStarRating('accuracy', ratings.accuracy)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Communication:</span>
              <span className="rating-stars">{renderStarRating('communication', ratings.communication)}</span>
            </div>
          </div>
        )}

        <div className="trade-actions">
          {trade.status === 'preparing' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContact}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-completed.svg`}
                    alt="Complete"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isCompletingTrade ? "Completing..." : "Complete Trade"}
                color="purple"
                onPress={handleCompleteTrade}
                isDisabled={isCompletingTrade || !!trade.awaitingFunding}
              />
            </>
          )}
          {trade.status === 'transit' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContact}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-completed.svg`}
                    alt="Complete"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isCompletingTrade ? "Completing..." : "Complete Trade"}
                color="purple"
                onPress={handleCompleteTrade}
                isDisabled={isCompletingTrade}
              />
            </>
          )}
          {trade.status === 'completed' && (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <MagicButton
                  icon={
                    <img
                      src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                      alt="Say thanks"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                    />
                  }
                  label="Say thanks"
                  color="dark-grey"
                  onPress={handleSayThanks}
                />
                <MagicButton
                  icon={
                    <img
                      src={arevakhachIcon}
                      alt="Trade Again"
                      style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  }
                  label="Trade again"
                  color="dark-grey"
                  onPress={handleTradeAgain}
                />
              </div>
              <div className="share-trade-btn">
                <MagicButton
                  icon={<ShareIcon size={18} color="white" />}
                  label="Share"
                  color="purple"
                  fullWidth
                  onPress={handleShareTrade}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="receiving-trade-card-right">
        <div className="trader-info-row">
          <div className="status-label-left">
            <span className="status-text">{getStatusLabel()}</span>
          </div>
          <div className="trader-info">
            {trade.status === 'preparing' && (
              <button
                className="send-points-btn"
                onClick={() => setIsSendPointsOpen(true)}
              >
                <img
                  src={AssetUrls.icons.profile.coin}
                  alt=""
                  className="send-points-btn-icon"
                />
                Send Points
              </button>
            )}
            <ViewModeDropdown<TradeDetailViewMode>
              className="trade-card-view-mode"
              options={TRADE_DETAIL_VIEW_OPTIONS}
              value={viewMode}
              onChange={setViewMode}
            />
            <AvatarWithFlag
              avatarUrl={trade.traderAvatar}
              username={trade.traderName}
              countryCode={trade.shippingAddress?.country}
              size={38}
              isElite={trade.traderIsElite}
              showAnimatedBorder={trade.traderShowAnimatedBorder}
              borderColor1={trade.traderBorderColor1}
              borderColor2={trade.traderBorderColor2}
            />
            <div className="trader-identity">
              <UsernameLink
                username={trade.traderName}
                isElite={trade.traderIsElite}
                className="trader-name trader-name--clickable"
              />
              <Points points={trade.pointBalance} type="small" />
            </div>
          </div>
        </div>

        <div className={`items-table items-table--${viewMode}`}>
          <div className="table-header">
            <span className="col-qty">Qty</span>
            <button
              type="button"
              className={`col-name col-sortable${sort.key === 'name' ? ` col-sortable--active col-sortable--${sort.dir}` : ''}`}
              onClick={() => handleHeaderSort('name')}
            >
              Name
              <SortArrow active={sort.key === 'name'} dir={sort.dir} />
            </button>
            <button
              type="button"
              className={`col-set col-sortable${sort.key === 'set' ? ` col-sortable--active col-sortable--${sort.dir}` : ''}`}
              onClick={() => handleHeaderSort('set')}
            >
              Set
              <SortArrow active={sort.key === 'set'} dir={sort.dir} />
            </button>
            <span className="col-foil">Foil</span>
            <span className="col-condition">Condition</span>
            <span className="col-language">Language</span>
            <button
              type="button"
              className={`col-points col-sortable${sort.key === 'points' ? ` col-sortable--active col-sortable--${sort.dir}` : ''}`}
              onClick={() => handleHeaderSort('points')}
            >
              Points
              <SortArrow active={sort.key === 'points'} dir={sort.dir} />
            </button>
          </div>

          {orderedItems.map(({ item, index }) => (
            <div key={index} className="table-row">
              <span className="col-qty">{item.qty}</span>
              <div
                className="col-name"
                onMouseEnter={(e) => handleCardHover(e, item)}
                onMouseMove={(e) => handleCardHover(e, item)}
                onMouseLeave={handleCardLeave}
              >
                {viewMode === 'list' ? (
                  <FoilCardImage
                    src={getCardImageUrl({ image: item.image, scryfallId: item.scryfallId }, 'art_crop')}
                    alt={item.name}
                    className="card-thumbnail card-thumbnail--art-crop"
                    finish={finishFromRow(item)}
                    borderRadius="3px"
                    onError={makeCardImageErrorHandler(
                      { image: item.image, scryfallId: item.scryfallId },
                      cardImageFallbackRef,
                      index,
                      'art_crop',
                    )}
                  />
                ) : (
                  <FoilCardImage
                    src={getCardImageUrl({ image: item.image, scryfallId: item.scryfallId }, 'small')}
                    alt={item.name}
                    className="card-thumbnail"
                    finish={finishFromRow(item)}
                    borderRadius="4px"
                    onError={makeCardImageErrorHandler(
                      { image: item.image, scryfallId: item.scryfallId },
                      cardImageFallbackRef,
                      index,
                      'small',
                    )}
                  />
                )}
                <span>{item.name}</span>
              </div>
              <div className="col-set" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <SetIcon setIconUrl={item.setIconUrl} setName={item.set} />
                <span>{item.setCode || item.set}{item.collectorNumber && <span style={{ color: '#888' }}> #{item.collectorNumber}</span>}</span>
              </div>
              <span className="col-foil"><FinishTag finish={finishFromRow(item)} size="cell" /></span>
              <span className="col-condition">{formatCondition(item.condition)}</span>
              <span className="col-language">{formatLanguage(item.language)}</span>
              <div className="col-points">
                <Points points={(item.pointValue || 0) * item.qty} type="small" />
              </div>
              {item.completedAt && (
                <div className="col-completed">
                  <span className="item-completed-badge" title="Card completed — sender credited">✓ Completed</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ContactUserModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        receiverId={parseInt(trade.traderId)}
        receiverUsername={trade.traderName}
        receiverAvatar={trade.traderAvatar}
        tradeId={trade.id}
      />

      <SendPointsModal
        isOpen={isSendPointsOpen}
        onClose={() => setIsSendPointsOpen(false)}
        recipientId={parseInt(trade.traderId)}
        recipientUsername={trade.traderName}
        tradeId={trade.id}
      />

      <FirstTradeConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmArrived}
        onCancel={() => setShowConfirmModal(false)}
        isCompletingTrade={isCompletingTrade}
      />

      <TradeShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareImageUrl={shareImageUrl}
        isLoadingImage={isGeneratingShareImage}
        receivedCards={trade.items}
      />

      {hoveredCard && (
        <CardImageTooltip
          scryfallId={hoveredCard.scryfallId}
          imageUrl={hoveredCard.imageUrl}
          cardName={hoveredCard.cardName}
          position={hoveredCard.position}
          onMouseEnter={() => {}}
          onMouseLeave={handleCardLeave}
        />
      )}
    </div>
  );
}